import { type ProspectTaskDto } from "@crowdcoursing/api/openapi";
import {
  CalendarMonth,
  CheckCircle,
  Delete,
  Flag,
  FlagOutlined,
  MoreVert,
  NotificationsActive,
  Person,
  Snooze,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Menu, MenuItem, Paper, Stack, Typography } from "@mui/material";
import Linkify from "linkify-react";
import { DateTime } from "luxon";
import { useState } from "react";

import { getDueDateColor, getPriorityLabel } from "../taskUtils";

const tMe = "Me";
const tSomeone = "Someone";

const getAssigneeChipLabel = (task: ProspectTaskDto, currentUserId: string | null | undefined): string => {
  if (!task.assignedToUserId) {
    return tSomeone;
  }
  if (currentUserId && task.assignedToUserId === currentUserId) {
    return tMe;
  }
  const name = [task.assignedToFirstName, task.assignedToLastName].filter(Boolean).join(" ").trim();
  return name || tSomeone;
};
const tCompleted = "Completed";
const tComplete = "Complete";
const tEdit = "Edit";
const tSnooze = "Snooze";
const tDelete = "Delete";

export interface TaskCardProps {
  task: ProspectTaskDto;
  /** When set, the assignee chip shows "Me" only if the task is assigned to this user. */
  currentUserId?: string | null;
  onComplete?: (taskId: string) => Promise<void>;
  onEdit?: (task: ProspectTaskDto) => void;
  onSnooze?: (taskId: string, task: ProspectTaskDto) => Promise<void>;
  onDelete?: (task: ProspectTaskDto) => void;
}

export const TaskCard = ({ task, currentUserId, onComplete, onEdit, onSnooze, onDelete }: TaskCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (error) {
      console.error("Error performing task action:", error);
    }
    handleClose();
  };

  const getPriorityIcon = (priority: number) => {
    return priority === 4 ? <FlagOutlined fontSize="small" /> : <Flag fontSize="small" />;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        maxWidth: "100%",
        overflow: "hidden",
        p: 2,
        position: "relative", // Position relative for absolute positioning of menu button
      }}
    >
      {/* Action menu positioned in top right corner */}
      <Box sx={{ position: "absolute", right: "8px", top: "8px" }}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="task actions"
          aria-controls={open ? "task-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <MoreVert />
        </IconButton>
        <Menu
          id="task-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            horizontal: "left",
            vertical: "bottom",
          }}
          transformOrigin={{
            horizontal: "left",
            vertical: "top",
          }}
        >
          {task.status !== 4 && onComplete && (
            <MenuItem onClick={() => handleAction(() => onComplete(task.id ?? ""))}>
              <CheckCircle sx={{ color: "success.main", mr: 1 }} />
              {tComplete}
            </MenuItem>
          )}

          {onEdit && (
            <MenuItem
              onClick={() => {
                handleClose();
                onEdit(task);
              }}
            >
              <Flag sx={{ color: "primary.main", mr: 1 }} />
              {tEdit}
            </MenuItem>
          )}

          {/* Only show snooze if due date is within next 24 hours */}
          {task.dueAt &&
            task.status !== 4 &&
            onSnooze &&
            DateTime.fromISO(task.dueAt).diff(DateTime.now(), "hours").hours <= 24 && (
              <MenuItem onClick={() => handleAction(() => onSnooze(task.id ?? "", task))}>
                <Snooze sx={{ color: "warning.main", mr: 1 }} />
                {tSnooze}
              </MenuItem>
            )}

          {onDelete && (
            <MenuItem
              onClick={() => {
                handleClose();
                onDelete(task);
              }}
              sx={{ color: "error.main" }}
            >
              <Delete sx={{ color: "error.main", mr: 1 }} />
              {tDelete}
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Box display="grid" gap={1} sx={{ pr: 4 }}>
        {" "}
        {/* Add right padding to prevent text from going under the menu button */}
        {/* Header with all chips */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={getPriorityIcon(task.priority ?? 4)}
            label={getPriorityLabel(task.priority ?? 4)}
            size="small"
            sx={{
              "& .MuiChip-icon": {
                color: "#fff",
              },
              backgroundColor: "primary.main",
              color: "#fff",
            }}
          />
          {!task.status || task.status !== 4 ? (
            <>
              {task.dueAt && (
                <Chip
                  icon={<CalendarMonth fontSize="small" />}
                  label={DateTime.fromISO(task.dueAt).toRelative()}
                  size="small"
                  sx={{
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                    ...getDueDateColor(task.dueAt),
                  }}
                />
              )}
              {task.reminderAt && (
                <Chip
                  icon={<NotificationsActive fontSize="small" />}
                  label={DateTime.fromISO(task.reminderAt).toRelative()}
                  size="small"
                  sx={{
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                    backgroundColor: "warning.main",
                    color: "#fff",
                  }}
                />
              )}
            </>
          ) : null}
          {task.assignedToUserId && (
            <Chip
              icon={<Person fontSize="small" />}
              label={getAssigneeChipLabel(task, currentUserId)}
              size="small"
              variant="outlined"
              sx={{
                "& .MuiChip-icon": {
                  color: "action.main",
                },
              }}
            />
          )}
          {task.status === 4 && (
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label={task.completedAt ? `${tCompleted} ${DateTime.fromISO(task.completedAt).toRelative()}` : tCompleted}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Stack>
        {/* Title and Description */}
        <Box sx={{ overflow: "hidden", width: "100%" }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, width: "100%", wordWrap: "break-word" }}>
            <Linkify
              options={{
                rel: "noopener noreferrer",
                style: {
                  display: "inline-block",
                  maxWidth: "100%",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                },
                target: "_blank",
              }}
            >
              {task.title}
            </Linkify>
          </Typography>
          {task.description && (
            <Typography variant="body2" color="text.secondary" sx={{ width: "100%", wordWrap: "break-word" }}>
              <Linkify
                options={{
                  rel: "noopener noreferrer",
                  style: {
                    display: "inline-block",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  },
                  target: "_blank",
                }}
              >
                {task.description}
              </Linkify>
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
